<script>
import EzButton from '@/components/ui/Button';
import EzForm from '@/components/ui/Form';
import { EzFormModal } from '@/components/ui/Modal';
import EzInput from '@/components/ui/Input';
import EzDropdown from '@/components/ui/Dropdown/EzDropdown';
import { EzTab, EzTabs } from '@/components/ui/Tabs';
import PrimaryContactToggle from '@/views/common/users/PrimaryContactToggle';
import OrderNotificationToggle from '@/views/common/users/OrderNotificationToggle';
import { LOADING_KEY, userModalTab1, userModalTab2 } from '@/util/constants';
import { mapGetters } from 'vuex';
import uuid from 'uuid/v4';
import { falsy } from '@/util/utils';

export default {
  components: {
    EzButton,
    EzForm,
    EzFormModal,
    EzInput,
    EzDropdown,
    EzTab,
    EzTabs,
    PrimaryContactToggle,
    OrderNotificationToggle,
  },
  props: {
    formKey: {
      type: String,
      required: true,
    },
    formAction: {
      type: String,
      required: true,
    },
    user: {
      type: Object,
      required: false,
      default: () => ({}),
    },
    additionalData: {
      type: Object,
      required: false,
      default: null,
    },
    entity: {
      type: String,
      required: false,
      default: 'User',
    },
    isEmailRequired: {
      type: Boolean,
      default: true,
    },
    isNameRequired: {
      type: Boolean,
      default: true,
    },
    types: {
      type: Array,
      default: null,
    },
    hasNotifications: {
      type: Boolean,
      default: true,
    },
    hasPrimaryToggle: {
      type: Boolean,
      default: true,
    },
    primaryImpliesOwner: {
      type: Boolean,
      default: false,
    },
    hasAtLeastOneDefaultPrimary: {
      type: Boolean,
      default: false,
    },
    isEditable: {
      type: Boolean,
      default: true,
    },
    shouldMirror: {
      type: Boolean,
      default: false,
    },
    gateSubmit: {
      type: Boolean,
      default: true,
    },
    forceNotificationsView: {
      type: Boolean,
      default: true,
    },
  },
  data() {
    return {
      keyId: null,
      loadingKey: LOADING_KEY.VENUE_SUPPLIER_ADD_USER,
      phoneValidatorVal: '',
      isPrimaryToggled: false,
      formValid: false,
      notificationsVisited: false,
      requiredValues: {
        name: null,
        ...(this.isEmailRequired ? { email: null } : {}),
        ...(this.types ? { type: null } : {}),
      },
      values: {
        name: null,
        email: null,
        phone: null,
        type: null,
      },
      settings: {
        email: false,
        whatsApp: false,
        sms: false,
      },
      userModalTab1,
      userModalTab2,
    };
  },
  computed: {
    ...mapGetters('errors', ['getFormErrors']),
    ...mapGetters('loading', ['getLoading']),
    getErrors() {
      return this.getFormErrors(this.formKey) || {};
    },
    hasErrors() {
      return !falsy(this.getErrors);
    },
    isChecked() {
      return this.user?.primary;
    },
    defaultData() {
      return {
        ...(this.hasPrimaryToggle ? { primary: this.isPrimaryToggled } : {}),
        optionalPermissions: [],
        ...this.additionalData,
      };
    },
    isExistingUser() {
      return !!this.user?.id;
    },
    method() {
      return this.isExistingUser ? 'patch' : 'post';
    },
    action() {
      if (!this.isExistingUser) return this.formAction;
      return `${this.formAction}/${this.user.id}`;
    },
    isLoadingUser() {
      return this.getLoading(LOADING_KEY.VENUE_SUPPLIER_ADD_USER);
    },
    emailLabel() {
      return `Email Address${this.isEmailRequired ? '*' : ''}`;
    },
    emailValidators() {
      return this.isEmailRequired ? 'email|required' : 'email';
    },
    nameLabel() {
      return `Name${this.isNameRequired ? '*' : ''}`;
    },
    nameValidators() {
      return this.isNameRequired ? 'required' : '';
    },
  },
  watch: {
    async user(val) {
      this.user = val;
      this.keyId = uuid();
      this.settings = { email: false, whatsApp: false, sms: false, ...this.user?.notificationSettings };
      if (!falsy(this.user)) {
        this.isPrimaryToggled = this.isChecked;
        await this.initialFormState();
        await this.validateForm();
      } else {
        this.isPrimaryToggled = false;
        this.requiredValues = this.buildRequired();
      }
    },
    isPrimaryToggled(val) {
      if (val) {
        this.phoneValidatorVal = 'required';
        this.requiredValues = {
          ...this.requiredValues,
          phone: (this.isExistingUser && !!this.user?.phone) || !!this.values.phone,
        };
      } else {
        this.phoneValidatorVal = '';
        this.requiredValues = { ...this.requiredValues, phone: true };
      }
      this.validateForm();
    },
    hasErrors(val) {
      if (val && this.$refs.tabs) this.$refs.tabs.selectTab(userModalTab1);
    },
  },
  methods: {
    buildRequired() {
      return {
        name: null,
        ...(this.isEmailRequired ? { email: null } : {}),
        ...(this.types ? { type: null } : {}),
      };
    },
    open() {
      this.notificationsVisited = false;
      if (this.$refs.tabs) this.$refs.tabs.selectTab(userModalTab1);
      this.$refs.userModal.open();
    },
    close() {
      this.resetValues();
      this.$refs.userModal.close();
    },
    resetValues() {
      this.values = {
        name: null,
        email: null,
        phone: null,
        type: null,
      };
    },
    onClose() {
      this.notificationsVisited = false;
      if (this.$refs.notifySettings) this.$refs.notifySettings.reset();
      this.$refs.form.clearErrors(['ezinput', 'eztextarea', 'ezdropdown', 'ezrolelist']);
      this.$refs.form.reset();
      this.$emit('close');
    },
    markNotificationsVisited() {
      this.notificationsVisited = true;
    },
    submitForm() {
      if (
        this.hasNotifications
        && this.forceNotificationsView
        && !this.notificationsVisited
        && !this.isExistingUser
      ) {
        this.$refs.tabs.selectTab(userModalTab2);
        this.notificationsVisited = true;
        return;
      }
      this.$refs.form.onSubmit();
    },
    onSubmitSuccess(result) {
      this.$emit('success', { ...result, isUpdate: this.isExistingUser });
      if (!this.isExistingUser) this.$refs.form.reset();
    },
    onSubmitError(result) {
      this.$emit('error', result);
    },
    primaryToggled(val) {
      this.isPrimaryToggled = val;
      this.validateForm();
    },
    async updateFormState(name, value) {
      this.values[name] = value;
      if (name === 'type') {
        this.requiredValues[name] = value?.id;
        this.validateForm();
        return;
      }
      this.requiredValues[name] = await this.$validator.validate(name, value);
      this.validateForm();
    },
    async initialFormState() {
      await this.$nextTick();
      this.requiredValues = {
        name: this.user.name,
        ...(this.isEmailRequired ? { email: this.user.email } : {}),
        ...(this.types ? { type: this.user.type } : {}),
        ...(this.isPrimaryToggled ? { phone: this.user.phone } : {}),
      };
      this.values = {
        name: this.user.name,
        email: this.user.email,
        phone: this.user.phone,
        type: this.user.type,
      };
    },
    validateForm() {
      if (!this.gateSubmit) {
        this.formValid = true;
        return;
      }
      if (this.isEmailRequired) {
        this.formValid = Object.keys(this.requiredValues).every(key => !!this.requiredValues[key]);
        return;
      }
      this.formValid = !!this.values.name
        && (!!this.values.email || !!this.values.phone)
        && (!this.types || !!this.values.type);
    },
    setNotifySettings(settings) {
      this.settings = { ...settings };
    },
    transformer(data) {
      const out = { ...data };
      if (!this.isEmailRequired && out.email === '') out.email = null;
      if (this.hasNotifications) out.notificationSettings = { ...this.settings };
      if (this.primaryImpliesOwner && this.isPrimaryToggled) out.type = 'owner';
      return out;
    },
  },
};
</script>

<template>
  <ez-form-modal
    ref="userModal"
    class="ez-contact-modal"
    :class="{ 'ez-contact-modal--no-tabs': !hasNotifications }"
    @close="onClose"
  >
    <template #title>
      <slot name="title">{{ isExistingUser ? `${entity} Details` : `Add New ${entity}` }}</slot>
    </template>
    <template #content>
      <ez-form
        :key="keyId"
        ref="form"
        :formKey="formKey"
        :additional-headers="{ loadingKey }"
        :action="action"
        :method="method"
        :default-data="defaultData"
        :transformer="transformer"
        @success="onSubmitSuccess"
        @error="onSubmitError"
      >
        <ez-tabs ref="tabs">
          <ez-tab :title="`${entity} Info`" :hash="userModalTab1">
            <ez-input
              class="mt-16"
              :formKey="formKey"
              :label="nameLabel"
              name="name"
              :validators="nameValidators"
              placeholder="Enter Full Name"
              :value="user.name"
              @onChange="updateFormState('name', $event)"
            />
            <ez-input
              :formKey="formKey"
              :label="emailLabel"
              placeholder="Enter Email Address"
              :validators="emailValidators"
              name="email"
              :value="user.email"
              @onChange="updateFormState('email', $event)"
            />
            <ez-input
              class="mt-12"
              :formKey="formKey"
              :value="user.phone"
              :validators="`${phoneValidatorVal}`"
              placeholder="Enter Phone Number"
              name="phone"
              label="Phone Number"
              @onChange="updateFormState('phone', $event)"
            />
            <ez-dropdown
              v-if="types"
              class="mt-12"
              :form-key="formKey"
              name="type"
              label="Contact Type*"
              placeholder="Select Contact Type"
              :data="types"
              is-full-width
              :selected="user.type"
              @change="updateFormState('type', $event)"
            />
            <input
              v-if="shouldMirror !== null"
              type="hidden"
              name="shouldMirror"
              :value="String(shouldMirror)"
            />
            <template v-if="hasPrimaryToggle">
              <hr />
              <primary-contact-toggle
                :key="user.id"
                :form-key="formKey"
                :disabled="isChecked && !hasAtLeastOneDefaultPrimary"
                :checked="isChecked"
                :value="String(isChecked)"
                @change="primaryToggled"
              />
            </template>
          </ez-tab>
          <ez-tab
            v-if="hasNotifications"
            title="Order Notifications"
            :hash="userModalTab2"
            @tabClick="markNotificationsVisited"
          >
            <order-notification-toggle
              ref="notifySettings"
              :user="user"
              :form-key="formKey"
              exclusive-sms-whats-app
              @change="setNotifySettings"
            />
          </ez-tab>
        </ez-tabs>
        <slot></slot>
      </ez-form>
    </template>
    <template #footer>
      <ez-button type="link" formType="button" @click="close">Cancel</ez-button>
      <ez-button
        v-tooltip.bottom="{
          content: !isEditable ? 'Please edit this user in the Group View' : '',
          classes: ['tooltip', 'tooltip--reset-margin'],
        }"
        :disabled="(gateSubmit && !formValid) || !isEditable"
        formType="button"
        :is-loading="isLoadingUser"
        @click="submitForm"
      >
        {{ isExistingUser ? 'Update Details' : `Add ${entity}` }}
      </ez-button>
    </template>
  </ez-form-modal>
</template>

<style scoped lang="scss">
.ez-contact-modal {
  :deep() .ez-dropdown {
    max-width: 100%;
  }
  :deep() .modal {
    overflow: visible;
  }
  .ez-form {
    .input-group + .input-group {
      margin-top: .75rem;
    }
  }
}
.ez-contact-modal--no-tabs {
  :deep() .ez-tabs__buttons {
    display: none;
  }
  :deep() .ez-tabs__content {
    border-top: 0;
  }
}
:deep() .ez-tabs {
  &__buttons {
    & > li:first-child {
      button {
        margin-left: 0;
      }
    }
  }
  &__content {
    overflow: inherit;
  }
}
</style>
